import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Grid, Chip, Alert, Snackbar, Divider, Avatar,
  List, ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Tabs, Tab, Autocomplete, InputAdornment, Switch, FormControlLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Delete as DeleteIcon,
  Notifications as NotificationsIcon, Repeat as RepeatIcon, Save as SaveIcon,
} from '@mui/icons-material';
import { eventsAPI, locationsAPI, type Event, type EventType, type City, type District, type MetroStation } from '../services/api';
import { useAuthStore } from '../store/authStore';
import InviteLinks from '../components/InviteLinks';

interface Participant { id: number; telegram_id: number; username: string | null; first_name: string | null; last_name: string | null; joined_at: string | null; }
interface Application { id: number; event_id: number; user_id: number; username: string | null; first_name: string | null; last_name: string | null; created_at: string; }

const ManageEventPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();

  const [event, setEvent] = useState<Event | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const [reminderDialog, setReminderDialog] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  // Edit form state
  const [editFormData, setEditFormData] = useState<any>({});
  const [editSaving, setEditSaving] = useState(false);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [metroStations, setMetroStations] = useState<MetroStation[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [eventData, participantsData, applicationsData] = await Promise.all([
          eventsAPI.getEvent(Number(id)),
          eventsAPI.getParticipants(Number(id)),
          eventsAPI.getApplications(Number(id)),
        ]);
        setEvent(eventData);
        setParticipants(participantsData);
        setApplications(applicationsData);

        setEditFormData({
          title: eventData.title,
          type_id: eventData.type_id,
          start_datetime: eventData.start_datetime.slice(0, 16),
          city_id: eventData.city_id,
          district_id: eventData.district_id || '',
          metro_station_id: eventData.metro_station_id || '',
          exact_address: eventData.exact_address || '',
          meeting_place: eventData.meeting_place || '',
          description: eventData.description || '',
          max_participants: eventData.max_participants,
          min_participants: eventData.min_participants,
          reserved_seats: eventData.reserved_seats,
          price: eventData.price,
          is_public: eventData.is_public,
        });

        if (searchParams.get('saved') === 'true') {
          setSnackbar({ open: true, message: 'Изменения сохранены!', severity: 'success' });
        }
      } catch (err: any) {
        setSnackbar({ open: true, message: 'Не удалось загрузить данные', severity: 'error' });
      } finally { setLoading(false); }
    };
    loadData();
  }, [id, searchParams]);

  // Load dictionaries for edit tab
  useEffect(() => {
    Promise.all([locationsAPI.getEventTypes(), locationsAPI.getAllCities()])
      .then(([types, allCities]) => { setEventTypes(types); setCities(allCities); })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (editFormData.city_id) {
      locationsAPI.getDistricts(Number(editFormData.city_id)).then(setDistricts).catch(() => setDistricts([]));
      locationsAPI.getMetroStations(Number(editFormData.city_id)).then(setMetroStations).catch(() => setMetroStations([]));
    }
  }, [editFormData.city_id]);

  const showSnackbar = (m: string, s: 'success' | 'error' | 'info' = 'info') => setSnackbar({ open: true, message: m, severity: s });
  const getErrorMessage = (err: any, def: string): string => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((e: any) => e.msg || e.message || String(e)).join('; ');
    return def;
  };
  const showConfirmDialog = (t: string, m: string, onConfirm: () => void) => setConfirmDialog({ open: true, title: t, message: m, onConfirm });
  const formatDate = (s: string) => new Date(s).toLocaleString('ru-RU', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const getStatusText = (s: string) => s === 'open' ? 'Набор открыт' : s === 'closed' ? 'Набор завершен' : 'Отменено';
  const getStatusColor = (s: string) => s === 'open' ? 'success' : s === 'closed' ? 'warning' : 'error';
  const getUserName = (p: { first_name: string | null; last_name: string | null; username: string | null }) => `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.username || 'Пользователь';

  const handleChangeStatus = async (status: 'open' | 'closed' | 'cancelled') => {
    const labels = { open: 'открыть регистрацию', closed: 'закрыть регистрацию', cancelled: 'отменить мероприятие' };
    showConfirmDialog('Подтверждение', `Вы уверены, что хотите ${labels[status]}?`, async () => {
      try {
        const result = await eventsAPI.changeEventStatus(Number(id), status);
        showSnackbar(result.message, 'success');
        setEvent(await eventsAPI.getEvent(Number(id)));
      } catch (err: any) { showSnackbar(getErrorMessage(err, 'Ошибка'), 'error'); }
    });
  };

  const handleApproveApplication = async (applicationId: number) => {
    try {
      await eventsAPI.approveApplication(applicationId);
      showSnackbar('Заявка одобрена', 'success');
      const [p, a, e] = await Promise.all([eventsAPI.getParticipants(Number(id)), eventsAPI.getApplications(Number(id)), eventsAPI.getEvent(Number(id))]);
      setParticipants(p); setApplications(a); setEvent(e);
    } catch (err: any) { showSnackbar(getErrorMessage(err, 'Ошибка'), 'error'); }
  };

  const handleRejectApplication = async (applicationId: number) => {
    showConfirmDialog('Отклонение', 'Отклонить заявку?', async () => {
      try {
        await eventsAPI.rejectApplication(applicationId);
        showSnackbar('Заявка отклонена', 'success');
        setApplications(await eventsAPI.getApplications(Number(id)));
      } catch (err: any) { showSnackbar(getErrorMessage(err, 'Ошибка'), 'error'); }
    });
  };

  const handleRemoveParticipant = async (participantId: number) => {
    showConfirmDialog('Удаление', 'Удалить участника?', async () => {
      try {
        await eventsAPI.removeParticipant(Number(id), participantId);
        showSnackbar('Участник удален', 'success');
        const [p, e] = await Promise.all([eventsAPI.getParticipants(Number(id)), eventsAPI.getEvent(Number(id))]);
        setParticipants(p); setEvent(e);
      } catch (err: any) { showSnackbar(getErrorMessage(err, 'Ошибка'), 'error'); }
    });
  };

  const handleSendReminder = async () => {
    try {
      const result = await eventsAPI.sendReminder(Number(id), reminderDialog.message || undefined);
      setReminderDialog({ open: false, message: '' });
      showSnackbar(result.message, 'success');
    } catch (err: any) { showSnackbar(getErrorMessage(err, 'Ошибка'), 'error'); }
  };

  const handleRepeatEvent = () => {
    if (!event) return;
    sessionStorage.setItem('repeat_event_data', JSON.stringify({
      title: event.title, type_id: event.type_id, city_id: event.city_id,
      district_id: event.district_id, metro_station_id: event.metro_station_id,
      exact_address: event.exact_address, meeting_place: event.meeting_place,
      description: event.description, max_participants: event.max_participants,
      min_participants: event.min_participants, reserved_seats: event.reserved_seats,
      price: event.price, is_public: event.is_public,
    }));
    navigate(`/events/create?repeat=1`);
  };

  const handleEditChange = (field: string, value: any) => {
    setEditFormData((prev: any) => ({ ...prev, [field]: value }));
    if (editErrors[field]) setEditErrors((prev: any) => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleSaveEdit = async () => {
    const errs: Record<string, string> = {};
    if (!editFormData.title || editFormData.title.length < 3) errs.title = 'Минимум 3 символа';
    if (!editFormData.type_id) errs.type_id = 'Выберите вид';
    if (!editFormData.start_datetime) errs.start_datetime = 'Укажите дату';
    if (!editFormData.city_id) errs.city_id = 'Выберите город';
    if (!editFormData.max_participants || editFormData.max_participants < 1) errs.max_participants = 'Минимум 1';
    setEditErrors(errs);
    if (Object.keys(errs).length > 0) { showSnackbar('Исправьте ошибки', 'error'); return; }

    try {
      setEditSaving(true);
      await eventsAPI.updateEvent(Number(id), {
        title: editFormData.title, type_id: Number(editFormData.type_id),
        start_datetime: editFormData.start_datetime, city_id: Number(editFormData.city_id),
        district_id: editFormData.district_id ? Number(editFormData.district_id) : null,
        metro_station_id: editFormData.metro_station_id ? Number(editFormData.metro_station_id) : null,
        exact_address: editFormData.exact_address, meeting_place: editFormData.meeting_place,
        description: editFormData.description, max_participants: editFormData.max_participants,
        min_participants: editFormData.min_participants, reserved_seats: editFormData.reserved_seats,
        price: editFormData.price, is_public: editFormData.is_public,
      });
      showSnackbar('Изменения сохранены!', 'success');
      setEvent(await eventsAPI.getEvent(Number(id)));
    } catch (err: any) { showSnackbar(getErrorMessage(err, 'Ошибка сохранения'), 'error'); }
    finally { setEditSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  if (!event) return <Box><Alert severity="error">Мероприятие не найдено</Alert><Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/events')} sx={{ mt: 2 }}>Назад</Button></Box>;

  const participantsCount = event.reserved_seats + event.current_participants;
  const isFull = participantsCount >= event.max_participants;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/events/${id}`)} size="small">Назад</Button>
          <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: '1rem', sm: '1.3rem' } }}>Управление</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <InviteLinks organizerId={user?.id || 0} eventId={Number(id)} organizerName={user?.first_name || 'Организатор'} />
          <Button variant="outlined" startIcon={<ViewIcon />} onClick={() => navigate(`/events/${id}`)} size="small">Просмотр</Button>
          <Button variant="contained" startIcon={<RepeatIcon />} onClick={handleRepeatEvent} size="small"
            sx={{ bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' } }}>Повторить</Button>
        </Box>
      </Box>

      {/* Event summary */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>{event.title}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              <Chip label={getStatusText(event.status)} color={getStatusColor(event.status) as any} size="small" />
              <Chip label={event.is_public ? 'Открытое' : 'Закрытое'} variant="outlined" size="small" />
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">{formatDate(event.start_datetime)}</Typography>
            <Typography variant="caption" display="block">{participantsCount}/{event.max_participants} участ.</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ '& .MuiTab-root': { fontWeight: 700, fontSize: '0.85rem', minHeight: 44 } }}>
          <Tab label={`Участники (${participants.length})`} />
          <Tab label={`Заявки (${applications.length})`} />
          <Tab label="Редактирование" />
          <Tab label="Действия" />
        </Tabs>
      </Paper>

      {/* Tab: Participants */}
      {tab === 0 && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
          {/* Progress */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption">Заполненность</Typography>
              <Typography variant="caption" fontWeight={700}>{Math.round((participantsCount / event.max_participants) * 100)}%</Typography>
            </Box>
            <Box sx={{ width: '100%', bgcolor: '#eee', borderRadius: 4, height: 6 }}>
              <Box sx={{ height: 6, borderRadius: 4, bgcolor: isFull ? '#2e7d32' : '#1565c0', width: `${Math.min((participantsCount / event.max_participants) * 100, 100)}%`, transition: 'width 0.3s' }} />
            </Box>
          </Box>

          {participants.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>Нет участников</Typography>
          ) : (
            <List sx={{ py: 0 }}>
              {participants.map(p => (
                <ListItem key={p.id} divider sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 36, height: 36, fontSize: '0.8rem' }}>{getUserName(p)[0]}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>{getUserName(p)}</Typography>}
                    secondary={<Typography variant="caption" color="text.secondary">{[p.username ? `@${p.username}` : '', p.joined_at ? `Записан: ${formatDate(p.joined_at)}` : ''].filter(Boolean).join(' • ')}</Typography>}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" color="error" onClick={() => handleRemoveParticipant(p.id)} size="small">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Tab: Applications */}
      {tab === 1 && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
          {applications.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>Нет заявок</Typography>
          ) : (
            <List sx={{ py: 0 }}>
              {applications.map(app => (
                <ListItem key={app.id} divider sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 36, height: 36, fontSize: '0.8rem' }}>{getUserName(app)[0]}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>{getUserName(app)}</Typography>}
                    secondary={<Typography variant="caption" color="text.secondary">Подана: {formatDate(app.created_at)}</Typography>}
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton edge="end" color="success" onClick={() => handleApproveApplication(app.id)} size="small"><CheckCircleIcon fontSize="small" /></IconButton>
                      <IconButton edge="end" color="error" onClick={() => handleRejectApplication(app.id)} size="small"><CancelIcon fontSize="small" /></IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Tab: Edit */}
      {tab === 2 && (
        <Paper sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField fullWidth required label="Название" value={editFormData.title || ''}
              onChange={e => handleEditChange('title', e.target.value)}
              error={!!editErrors.title} helperText={editErrors.title} size="small" inputProps={{ maxLength: 100 }} />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Autocomplete fullWidth options={eventTypes}
                getOptionLabel={o => `${o.icon_emoji || ''} ${o.name}`}
                renderInput={params => <TextField {...params} label="Вид" size="small" error={!!editErrors.type_id} helperText={editErrors.type_id} />}
                value={eventTypes.find(t => t.id === editFormData.type_id) || null}
                onChange={(_, v) => handleEditChange('type_id', v?.id || '')}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
              />
              <TextField type="datetime-local" label="Дата" value={editFormData.start_datetime || ''}
                onChange={e => handleEditChange('start_datetime', e.target.value)}
                error={!!editErrors.start_datetime} helperText={editErrors.start_datetime}
                InputLabelProps={{ shrink: true }} size="small" sx={{ minWidth: 200 }} />
            </Box>

            <Autocomplete fullWidth options={cities}
              getOptionLabel={o => `${o.name} ${o.region ? `(${o.region})` : ''}`}
              renderInput={params => <TextField {...params} label="Город" size="small" error={!!editErrors.city_id} helperText={editErrors.city_id} />}
              value={cities.find(c => c.id === editFormData.city_id) || null}
              onChange={(_, v) => handleEditChange('city_id', v?.id || '')}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Autocomplete fullWidth options={districts} getOptionLabel={o => o.name}
                disabled={!editFormData.city_id || districts.length === 0}
                renderInput={params => <TextField {...params} label="Район" size="small" />}
                value={districts.find(d => d.id === editFormData.district_id) || null}
                onChange={(_, v) => handleEditChange('district_id', v?.id || '')}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
              />
              <Autocomplete fullWidth options={metroStations} getOptionLabel={o => o.name}
                disabled={!editFormData.city_id || metroStations.length === 0}
                renderInput={params => <TextField {...params} label="Метро" size="small" />}
                value={metroStations.find(s => s.id === editFormData.metro_station_id) || null}
                onChange={(_, v) => handleEditChange('metro_station_id', v?.id || '')}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
              />
            </Box>

            <TextField fullWidth label="Точный адрес" value={editFormData.exact_address || ''}
              onChange={e => handleEditChange('exact_address', e.target.value)} size="small" helperText="Виден только участникам" />

            <TextField fullWidth label="Место встречи" value={editFormData.meeting_place || ''}
              onChange={e => handleEditChange('meeting_place', e.target.value)} size="small" multiline rows={2} />

            <TextField fullWidth multiline rows={4} label="Описание" value={editFormData.description || ''}
              onChange={e => handleEditChange('description', e.target.value)} size="small"
              inputProps={{ maxLength: 1000 }} helperText={`${(editFormData.description || '').length}/1000`} />

            <Divider />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField type="number" label="Мин. участников" value={editFormData.min_participants || 1}
                onChange={e => handleEditChange('min_participants', Number(e.target.value))} size="small" sx={{ flex: 1, minWidth: 120 }} inputProps={{ min: 1 }} />
              <TextField type="number" label="Макс. участников" value={editFormData.max_participants || 10}
                onChange={e => handleEditChange('max_participants', Number(e.target.value))} size="small" sx={{ flex: 1, minWidth: 120 }}
                error={!!editErrors.max_participants} helperText={editErrors.max_participants} inputProps={{ min: 1 }} />
              <TextField type="number" label="Зарезервировано" value={editFormData.reserved_seats || 0}
                onChange={e => handleEditChange('reserved_seats', Number(e.target.value))} size="small" sx={{ flex: 1, minWidth: 120 }} inputProps={{ min: 0 }} />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField type="number" label="Цена" value={editFormData.price || 0}
                onChange={e => handleEditChange('price', Number(e.target.value))} size="small" sx={{ flex: 1, minWidth: 120 }}
                InputProps={{ endAdornment: <InputAdornment position="end">₽</InputAdornment> }} inputProps={{ min: 0 }} />
              <FormControlLabel
                control={<Switch checked={editFormData.is_public || false} onChange={e => handleEditChange('is_public', e.target.checked)} size="small" />}
                label={<Typography variant="body2">{editFormData.is_public ? 'Открытое' : 'Закрытое'}</Typography>}
              />
            </Box>

            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveEdit} disabled={editSaving}
              sx={{ alignSelf: 'flex-start', bgcolor: '#ef6c00', '&:hover': { bgcolor: '#e65100' } }}>
              {editSaving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Tab: Actions */}
      {tab === 3 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Status */}
          <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Статус регистрации</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {event.status === 'open' && (
                <Button variant="outlined" color="warning" size="small" onClick={() => handleChangeStatus('closed')}>Закрыть регистрацию</Button>
              )}
              {event.status === 'closed' && (
                <Button variant="outlined" color="success" size="small" onClick={() => handleChangeStatus('open')}>Открыть регистрацию</Button>
              )}
              {event.status !== 'cancelled' && (
                <Button variant="outlined" color="error" size="small" onClick={() => handleChangeStatus('cancelled')}>Отменить</Button>
              )}
            </Box>
            {event.status === 'cancelled' && <Alert severity="error" sx={{ mt: 1 }}>Мероприятие отменено</Alert>}
          </Paper>

          {/* Reminder */}
          <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Напоминание</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              Отправить напоминание {participants.length} участникам
            </Typography>
            <Button variant="outlined" startIcon={<NotificationsIcon />} size="small"
              onClick={() => setReminderDialog({ open: true, message: '' })}
              disabled={participants.length === 0 || event.status === 'cancelled'}>
              Отправить напоминание
            </Button>
          </Paper>

          {/* Stats summary */}
          <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Статистика</Typography>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={800} color="primary">{participantsCount}</Typography>
                  <Typography variant="caption" color="text.secondary">Участников</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={800} color="warning.main">{applications.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Заявок</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={800} color="success.main">{event.max_participants - participantsCount}</Typography>
                  <Typography variant="caption" color="text.secondary">Свободных</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}

      {/* Dialogs */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent><Typography>{confirmDialog.message}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Отмена</Button>
          <Button onClick={() => { confirmDialog.onConfirm(); setConfirmDialog({ ...confirmDialog, open: false }); }} variant="contained" color="error">Подтвердить</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reminderDialog.open} onClose={() => setReminderDialog({ open: false, message: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Напоминание участникам</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Будет отправлено {participants.length} участникам</Typography>
          <TextField fullWidth multiline rows={3} label="Текст (необязательно)" value={reminderDialog.message}
            onChange={e => setReminderDialog({ ...reminderDialog, message: e.target.value })}
            placeholder="Не забудьте взять воду и удобную обувь!" size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderDialog({ open: false, message: '' })}>Отмена</Button>
          <Button onClick={handleSendReminder} variant="contained">Отправить</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageEventPage;